import React, { useState, useEffect, useRef } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { messagesAPI } from '../services/api';
import { signalRService } from '../services/signalr';
import { Conversation, Message as MessageType } from '../types/message';
import { useAuth } from '../contexts/AuthContext';
import { PaperAirplaneIcon, MagnifyingGlassIcon, ChevronLeftIcon } from '@heroicons/react/24/outline';

const Messages: React.FC = () => {
  const { user } = useAuth();
  usePageTitle('Üzenetek');
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<MessageType[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadConversations();

    // Setup SignalR listeners
    const unsubscribe = signalRService.onMessage(async (message) => {
      if (selectedConversation &&
          (message.senderUserId === selectedConversation.userId ||
           message.receiverUserId === selectedConversation.userId)) {

        // Check if message already exists (update it) or add new
        setMessages((prev) => {
          const existingIndex = prev.findIndex(m => m.id === message.id);
          if (existingIndex >= 0) {
            // Update existing message (e.g., readAt changed)
            const updated = [...prev];
            updated[existingIndex] = message;
            return updated;
          }
          // Add new message
          return [...prev, message];
        });

        // Auto-mark as read if received from the current conversation
        if (message.senderUserId === selectedConversation.userId && message.receiverUserId === user?.id && !message.readAt) {
          try {
            await messagesAPI.markAsRead(message.id);
            await signalRService.markAsRead(message.id);
          } catch (error) {
            console.error('Failed to mark message as read:', error);
          }
        }
      }
      // Refresh conversations to update last message and unread count
      loadConversations();
    });

    return () => unsubscribe();
  }, [selectedConversation, user?.id]);

  // Auto-scroll when messages change
  useEffect(() => {
    if (messages.length > 0) {
      setTimeout(() => scrollToBottom(), 100);
    }
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversations = async () => {
    try {
      console.log('Loading conversations...');
      const data = await messagesAPI.getConversations();
      console.log('Conversations loaded:', data);
      setConversations(data);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (conversation: Conversation) => {
    try {
      setSelectedConversation(conversation);
      const data = await messagesAPI.getConversation(conversation.userId);
      setMessages(data);

      // Mark messages as read
      const unreadMessages = data.filter(m => m.receiverUserId === user?.id && !m.readAt);
      for (const msg of unreadMessages) {
        await messagesAPI.markAsRead(msg.id);
        await signalRService.markAsRead(msg.id);
      }

      setTimeout(scrollToBottom, 100);

      // Focus the input field
      setTimeout(() => messageInputRef.current?.focus(), 150);
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    try {
      await messagesAPI.sendMessage(selectedConversation.userId, newMessage);
      setNewMessage('');
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();

    // Check if today
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      return date.toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' });
    }

    // Check if same year
    const isSameYear = date.getFullYear() === now.getFullYear();

    if (isSameYear) {
      return date.toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' });
    }

    // Different year
    return date.toLocaleDateString('hu-HU', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatMessageTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();

    // Check if today
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      return date.toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' });
    }

    // Check if same year
    const isSameYear = date.getFullYear() === now.getFullYear();

    if (isSameYear) {
      return date.toLocaleDateString('hu-HU', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    }

    // Different year
    return date.toLocaleDateString('hu-HU', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const filteredConversations = conversations.filter(conv =>
    conv.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conv.userEmail.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">Betöltés...</div>
      </div>
    );
  }

  return (
    <div className="relative flex h-[calc(100dvh-11.5rem)] overflow-hidden bg-gray-50 lg:h-[calc(100vh-4rem)] md:overflow-visible">
      {/* Conversations List */}
      <div className="w-full md:w-80 bg-white md:border-r border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold mb-3">Üzenetek</h2>
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Keresés..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {filteredConversations.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              {searchQuery ? 'Nincs találat' : 'Nincsenek felhasználók'}
            </div>
          ) : (
            filteredConversations.map((conv) => (
              <button
                key={conv.userId}
                onClick={() => loadMessages(conv)}
                className={`w-full p-3 border-b border-gray-100 hover:bg-gray-50 text-left transition-colors ${
                  selectedConversation?.userId === conv.userId ? 'bg-primary-50' : ''
                }`}
              >
                <div className="flex items-center space-x-3">
                  {/* Avatar */}
                  <div className="flex-shrink-0">
                    <div className="h-12 w-12 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white font-semibold text-sm shadow-sm">
                      {getInitials(conv.userName)}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-0.5">
                      <h3 className="font-semibold text-gray-900 truncate">{conv.userName}</h3>
                      {conv.lastMessage && (
                        <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                          {formatTime(conv.lastMessage.sentAt)}
                        </span>
                      )}
                    </div>
                    {conv.lastMessage ? (
                      <p className="text-sm text-gray-600 truncate">{conv.lastMessage.content}</p>
                    ) : (
                      <p className="text-sm text-gray-400 italic">Nincs még üzenet</p>
                    )}
                  </div>

                  {/* Unread badge */}
                  {conv.unreadCount > 0 && (
                    <div className="flex-shrink-0">
                      <span className="bg-primary-600 text-white text-xs font-semibold rounded-full h-5 min-w-[20px] px-1.5 flex items-center justify-center">
                        {conv.unreadCount}
                      </span>
                    </div>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Chat Area – mobilon jobbról beúszó panel, md-től a megszokott oldalsó elrendezés */}
      <div
        className={`absolute inset-0 z-10 flex flex-col bg-gray-50 transform transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${
          selectedConversation ? 'translate-x-0' : 'translate-x-full'
        } md:static md:z-auto md:flex-1 md:translate-x-0 md:transition-none`}
      >
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="bg-white border-b border-gray-200 p-4 flex items-center gap-2">
              <button
                type="button"
                onClick={() => setSelectedConversation(null)}
                aria-label="Vissza a beszélgetésekhez"
                className="md:hidden flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <ChevronLeftIcon className="h-6 w-6" />
              </button>
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-gray-900 truncate">{selectedConversation.userName}</h2>
                <p className="text-sm text-gray-500 truncate">{selectedConversation.userEmail}</p>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((message, index) => {
              const isOwn = message.senderUserId === user?.id;

              // Find last read message by current user
              const isLastReadMessage = isOwn && message.readAt && (
                index === messages.length - 1 ||
                messages.slice(index + 1).every(m => m.senderUserId !== user?.id || !m.readAt)
              );

              return (
                <div
                  key={message.id}
                  className={`flex flex-col ${isOwn ? 'items-end' : 'items-start'}`}
                >
                  {/* Timestamp and read status */}
                  <div className="text-xs text-gray-500 mb-1 px-1">
                    {isLastReadMessage && <span className="mr-2">Olvasva ·</span>}
                    {formatMessageTime(message.sentAt)}
                  </div>

                  {/* Message bubble */}
                  <div
                    className={`max-w-xs lg:max-w-md xl:max-w-lg px-4 py-2 rounded-2xl shadow-sm ${
                      isOwn
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-900'
                    }`}
                  >
                    <p className="break-words">{message.content}</p>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <form onSubmit={sendMessage} className="bg-white border-t border-gray-200 p-4">
              <div className="flex items-center space-x-2">
                <input
                  ref={messageInputRef}
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Írj egy üzenetet..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <button
                  type="submit"
                  disabled={!newMessage.trim()}
                  className="flex h-9 w-9 items-center justify-center bg-primary-600 text-white rounded-full hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <PaperAirplaneIcon className="h-5 w-5" />
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="hidden md:flex flex-1 items-center justify-center bg-gray-50">
            <div className="text-center text-gray-500">
              <p className="text-lg">Válassz ki egy beszélgetést</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Messages;
